import React, { useCallback, useEffect, useRef, useState } from 'react';
import { RichTextEditor } from 'mui-tiptap';
import { useSetProjectGeneralInfoMutation } from 'src/api/v1/project-settings-ApiSlice';
import { generalInfoEditorExtensions } from 'src/pages/ProjectSettingsPage/GeneralInfoEditor/generalInfoEditorExtensions';
import { GeneralInfoMenuControls } from 'src/pages/ProjectSettingsPage/GeneralInfoEditor/GeneralInfoMenuControls';
import { GeneralInfoTableBubbleMenu } from 'src/pages/ProjectSettingsPage/GeneralInfoEditor/GeneralInfoTableBubbleMenu';
import { useSaveFeedback } from 'src/pages/ProjectSettingsPage/useSaveFeedback';

type Props = {
    projectId: number;
    initialContent: string;
    editable: boolean;
};

const SAVE_DEBOUNCE_MS = 4000;
const EDITOR_MIN_HEIGHT = 200;

const renderControls = () => <GeneralInfoMenuControls />;

export const GeneralInfoEditor = ({ projectId, initialContent, editable }: Props) => {
    const [content, setContent] = useState(initialContent);
    // Not state on purpose: a failed save must not re-arm the debounce, only the next edit or blur retries
    const lastSentContentRef = useRef<string | null>(initialContent);
    const [setProjectGeneralInfo] = useSetProjectGeneralInfoMutation();
    const { showSaveSuccess, showSaveError } = useSaveFeedback();

    const saveContent = useCallback((generalInfo: string) => {
        lastSentContentRef.current = generalInfo;
        setProjectGeneralInfo({ projectId, generalInfo })
            .unwrap()
            .then(showSaveSuccess)
            .catch(() => {
                lastSentContentRef.current = null;
                showSaveError();
            });
    }, [projectId, setProjectGeneralInfo, showSaveSuccess, showSaveError]);

    useEffect(() => {
        if (content === lastSentContentRef.current) {
            return;
        }
        const timer = setTimeout(() => saveContent(content), SAVE_DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [content, saveContent]);

    const handleBlur = () => {
        if (content !== lastSentContentRef.current) {
            saveContent(content);
        }
    };

    return (
        <RichTextEditor
            extensions={generalInfoEditorExtensions}
            content={initialContent}
            editable={editable}
            renderControls={renderControls}
            onUpdate={({ editor }) => setContent(editor.getHTML())}
            onBlur={handleBlur}
            RichTextFieldProps={{
                RichTextContentProps: { sx: { minHeight: EDITOR_MIN_HEIGHT } },
            }}
        >
            {() => <GeneralInfoTableBubbleMenu />}
        </RichTextEditor>
    );
};
